import * as React from "react";
import { observer } from "mobx-react-lite";
import { Layout, message, Tabs } from "antd";
import type { MenuProps } from "antd";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import TaskStatusCreateDialog from "./TaskStatusCreateDialog";
import TaskStatusEditDialog from "./TaskStatusEditDialog";
import CustomizeWorkflowModal from "./CustomizeWorkflowModal";
import RevertWorkflowModal from "./RevertWorkflowModal";
import RemoveSeparateSubtaskStatusesModal from "./RemoveSeparateSubtaskStatusesModal";
import SeparateSubtaskStatusesModal from "./SeparateSubtaskStatusesModal";
import TaskStatusFlowEditor from "./TaskStatusFlowEditor";
import { useForm } from "antd/lib/form/Form";
import {
    TaskStatusFlowModeStatusMapping,
    TaskSubtaskStatusMapping,
} from "../../task_types/types";
import TaskStatusesHeader, {
    TaskStatusFlowActions,
} from "./TaskStatusesHeader";
import TaskStatusesFilters from "./TaskStatusesFilters";
import TaskStatusesTabContent from "./TaskStatusesTabContent";
import TaskTypeCreateDialog from "../../task_types/components/TaskTypeCreateDialog";
import { useTaskTypesStore } from "../../task_types/stores/context";

const { Content } = Layout;

type Props = {
    store: TaskStatusesVisualStore;
};

const TaskStatusesList: React.FC<Props> = ({ store }) => {
    const taskTypesContext = useTaskTypesStore();
    const taskTypesStore = taskTypesContext.store;
    const [currentTab, setCurrentTab] = React.useState("1");
    const [isCustomizeWorkflowModalOpen, setIsCustomizeWorkflowModalOpen] =
        React.useState(false);
    const [isRevertWorkflowModalOpen, setIsRevertWorkflowModalOpen] =
        React.useState(false);
    const [
        isSeparateSubtaskStatusesModalOpen,
        setIsSeparateSubtaskStatusesModalOpen,
    ] = React.useState(false);
    const [
        isRemoveSeparateSubtaskStatusesModalOpen,
        setIsRemoveSeparateSubtaskStatusesModalOpen,
    ] = React.useState(false);
    const [isStatusFlowModeUpdating, setIsStatusFlowModeUpdating] =
        React.useState(false);
    const [isCreatingSubtaskStatus, setIsCreatingSubtaskStatus] =
        React.useState(false);
    const [
        isDistinctSubtaskStatusesUpdating,
        setIsDistinctSubtaskStatusesUpdating,
    ] = React.useState(false);
    const [flowActions, setFlowActions] =
        React.useState<TaskStatusFlowActions | null>(null);
    const [form] = useForm();
    const isCustomMode = store.taskType?.statusFlowMode === "custom";
    const showCustomizeWorkflowButton = !store.taskType?.isSystemDefault;
    const areActionsEnabled =
        !!store.selectedProjectId && !!store.selectedTaskType;

    React.useEffect(() => {
        if (store.selectedProjectId) {
            store.loadTaskTypes(store.selectedProjectId);
            store.loadActionDefinitions(store.selectedProjectId);
            store.loadUsers();
        }
    }, [store.selectedProjectId, store]);

    React.useEffect(() => {
        if (!store.selectedProjectId) {
            taskTypesStore.clearSelectedProject();
            return;
        }

        if (taskTypesStore.selectedProjectId === store.selectedProjectId) {
            return;
        }

        void taskTypesStore.setSelectedProjectId(store.selectedProjectId);
    }, [store.selectedProjectId, taskTypesStore]);

    React.useEffect(() => {
        setIsCustomizeWorkflowModalOpen(false);
        setIsRevertWorkflowModalOpen(false);
        setIsSeparateSubtaskStatusesModalOpen(false);
        setIsRemoveSeparateSubtaskStatusesModalOpen(false);
    }, [store.selectedProjectId, store.selectedTaskType]);

    const tabItems = [
        {
            key: "1",
            label: (
                <span data-id-type="Tab" data-id-value="List">
                    List
                </span>
            ),
            children: (
                <TaskStatusesTabContent
                    areActionsEnabled={areActionsEnabled}
                    store={store}
                    isCustomMode={isCustomMode}
                    onCreateCustomTaskType={() => {
                        taskTypesStore.setNewTypeDialogVisible(true);
                    }}
                    onAddTaskStatus={() => {
                        setIsCreatingSubtaskStatus(false);
                        store.setNewStatusDialogVisible(true);
                    }}
                    onAddSubtaskStatus={() => {
                        setIsCreatingSubtaskStatus(true);
                        store.setNewStatusDialogVisible(true);
                    }}
                    onOpenSeparateSubtaskStatusesModal={() => {
                        setIsSeparateSubtaskStatusesModalOpen(true);
                    }}
                    onOpenRemoveSeparateSubtaskStatusesModal={() => {
                        setIsRemoveSeparateSubtaskStatusesModalOpen(true);
                    }}
                />
            ),
        },
        {
            key: "2",
            label: (
                <span data-id-type="Tab" data-id-value="Flow">
                    Flow
                </span>
            ),
            children: store.selectedTaskType && currentTab === "2" && (
                <TaskStatusFlowEditor
                    form={form}
                    store={store}
                    isCustomMode={isCustomMode}
                    areActionsEnabled={areActionsEnabled}
                    onCreateCustomTaskType={() => {
                        taskTypesStore.setNewTypeDialogVisible(true);
                    }}
                    onActionsChange={setFlowActions}
                />
            ),
        },
    ];

    const handleEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        store.setSearchString(event.currentTarget.value);
    };

    const handleSaveChanges = React.useCallback(async () => {
        if (currentTab === "2") {
            await flowActions?.saveFlow();
        }
    }, [currentTab, flowActions]);

    const handleSwitchToCustomMode = React.useCallback(async () => {
        setIsStatusFlowModeUpdating(true);
        try {
            const updated = await store.updateTaskTypeStatusFlowMode("custom");
            if (updated) {
                setIsCustomizeWorkflowModalOpen(false);
            }
        } finally {
            setIsStatusFlowModeUpdating(false);
        }
    }, [store]);

    const handleSwitchToStandardMode = React.useCallback(
        async (statusMappings: TaskStatusFlowModeStatusMapping[]) => {
            setIsStatusFlowModeUpdating(true);
            try {
                const updated = await store.updateTaskTypeStatusFlowMode(
                    "standard",
                    statusMappings,
                );
                if (updated) {
                    setIsRevertWorkflowModalOpen(false);
                }
            } finally {
                setIsStatusFlowModeUpdating(false);
            }
        },
        [store],
    );

    const handleCancelEditing = React.useCallback(async () => {
        await flowActions?.cancelFlowChanges();
    }, [flowActions]);

    const handleDistinctSubtaskStatusesChange = React.useCallback(
        async (
            checked: boolean,
            subtaskStatusMappings?: TaskSubtaskStatusMapping[],
        ) => {
            setIsDistinctSubtaskStatusesUpdating(true);
            try {
                await store.updateDistinctSubtaskStatuses(
                    checked,
                    subtaskStatusMappings,
                );
            } finally {
                setIsDistinctSubtaskStatusesUpdating(false);
            }
        },
        [store],
    );

    const switchBackMenuItem: NonNullable<MenuProps["items"]>[number] = {
        key: "switch-to-standard-workflow",
        label: (
            <span style={{ color: "#C85F4D" }}>
                Switch back to standard workflow
            </span>
        ),
        disabled: !areActionsEnabled,
        onClick: () => setIsRevertWorkflowModalOpen(true),
    };

    const customModeListActionsMenu: MenuProps = {
        items: [switchBackMenuItem],
    };

    const actionsMenu = isCustomMode
        ? customModeListActionsMenu
        : { items: [] };

    const shouldShowMoreActionsButton = isCustomMode;

    return (
        <Layout className="screen-size layout-with-table">
            <TaskStatusCreateDialog
                store={store}
                isSubtaskStatus={isCreatingSubtaskStatus}
            />
            <TaskTypeCreateDialog
                store={taskTypesStore}
                statusFlowMode="custom"
                onCreated={async (taskTypeId) => {
                    await store.setSelectedTaskType(taskTypeId);
                    message.success(
                        "Task type created. You can now configure its workflow.",
                    );
                }}
            />
            <CustomizeWorkflowModal
                open={isCustomizeWorkflowModalOpen}
                isLoading={isStatusFlowModeUpdating}
                taskTypeName={store.taskType?.name}
                onCancel={() => setIsCustomizeWorkflowModalOpen(false)}
                onConfirm={() => {
                    handleSwitchToCustomMode().catch((err) =>
                        console.error(err),
                    );
                }}
            />
            <RevertWorkflowModal
                open={isRevertWorkflowModalOpen}
                isLoading={isStatusFlowModeUpdating}
                taskTypeName={store.taskType?.name}
                statuses={store.taskStatuses.filter(
                    (status) => !status.isSubtaskStatus,
                )}
                onCancel={() => setIsRevertWorkflowModalOpen(false)}
                onConfirm={(statusMappings) => {
                    handleSwitchToStandardMode(statusMappings).catch((err) =>
                        console.error(err),
                    );
                }}
            />
            <SeparateSubtaskStatusesModal
                open={isSeparateSubtaskStatusesModalOpen}
                isLoading={isDistinctSubtaskStatusesUpdating}
                onCancel={() => setIsSeparateSubtaskStatusesModalOpen(false)}
                onConfirm={() => {
                    handleDistinctSubtaskStatusesChange(true)
                        .then(() => {
                            setIsSeparateSubtaskStatusesModalOpen(false);
                        })
                        .catch((err) => console.error(err));
                }}
            />
            <RemoveSeparateSubtaskStatusesModal
                open={isRemoveSeparateSubtaskStatusesModalOpen}
                isLoading={isDistinctSubtaskStatusesUpdating}
                taskStatuses={store.taskStatuses}
                onCancel={() =>
                    setIsRemoveSeparateSubtaskStatusesModalOpen(false)
                }
                onConfirm={(statusMappings) => {
                    handleDistinctSubtaskStatusesChange(false, statusMappings)
                        .then(() => {
                            setIsRemoveSeparateSubtaskStatusesModalOpen(false);
                        })
                        .catch((err) => console.error(err));
                }}
            />
            <TaskStatusesHeader
                currentTab={currentTab}
                flowActions={flowActions}
                isCustomMode={isCustomMode}
                areActionsEnabled={areActionsEnabled}
                showCustomizeWorkflowButton={showCustomizeWorkflowButton}
                actionsMenu={actionsMenu}
                shouldShowMoreActionsButton={shouldShowMoreActionsButton}
                onOpenCustomizeWorkflow={() =>
                    setIsCustomizeWorkflowModalOpen(true)
                }
                onCancelEditing={() => {
                    handleCancelEditing().catch((err) => console.error(err));
                }}
                onSaveChanges={() => {
                    handleSaveChanges().catch((err) => console.error(err));
                }}
            />
            <TaskStatusEditDialog store={store} />
            <Layout style={{ minHeight: 0 }}>
                <Content
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}
                >
                    <TaskStatusesFilters
                        currentTab={currentTab}
                        isCustomMode={isCustomMode}
                        store={store}
                        onSearchEnter={handleEnterPress}
                    />
                    <Tabs
                        onChange={(key) => setCurrentTab(key)}
                        className="statuses-tabs-wrapper"
                        style={{ flex: 1, minHeight: 0 }}
                        items={tabItems}
                    />
                </Content>
            </Layout>
        </Layout>
    );
};

export default observer(TaskStatusesList);
